import { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, FileText, CheckCircle, AlertTriangle, Package, ClipboardList, Star, Layers } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ReconciliationPage() {
  const { woId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [reconciliationData, setReconciliationData] = useState(null);
  const [items, setItems] = useState([]);
  const [gradingItems, setGradingItems] = useState([]);
  const [notes, setNotes] = useState("");
  const [shippedDate, setShippedDate] = useState(new Date().toISOString().split('T')[0]);
  const [showReport, setShowReport] = useState(false);
  const [report, setReport] = useState(null);
  const [activeTab, setActiveTab] = useState("reconciliation");
  const [accessoriesItems, setAccessoriesItems] = useState([]);

  useEffect(() => {
    fetchReconciliationData();
  }, [woId]);

  const fetchReconciliationData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/work-orders/${woId}/reconciliation`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setReconciliationData(response.data);
      
      // Initialize items with size data including production quantities
      const initialItems = response.data.size_data.map(item => ({
        size: item.size,
        quality: item.quality,
        gsm: item.gsm,
        ordered_qty: item.ordered_qty,
        weaved_qty: item.weaved_qty || 0,
        dyed_qty: item.dyed_qty || 0,
        stitched_qty: item.stitched_qty || 0,
        shipped_qty: item.shipped_qty || item.stitched_qty || 0,
        remarks: ""
      }));
      setItems(initialItems);
      
      // Initialize grading items with weaved_qty
      const existingGrading = response.data.existing_reconciliation?.grading || {};
      const initialGradingItems = response.data.size_data.map(item => {
        const sizeKey = `${item.size}-${item.quality}-${item.gsm}`;
        const existingGrade = existingGrading[sizeKey] || {};
        return {
          size: item.size,
          quality: item.quality,
          gsm: item.gsm,
          weaved_qty: item.weaved_qty || 0,
          dyed_qty: item.dyed_qty || 0,
          stitched_qty: item.stitched_qty || 0,
          shipped_qty: item.shipped_qty || item.stitched_qty || 0,
          fresh_pcs: existingGrade.fresh_pcs || '',
          b_grade: existingGrade.b_grade || '',
          stain: existingGrade.stain || '',
          s2: existingGrade.s2 || ''
        };
      });
      setGradingItems(initialGradingItems);
      
      // Initialize accessories items from WO accessories data
      const existingAccRecon = response.data.existing_reconciliation?.accessories_reconciliation || {};
      const woAccessories = response.data.accessories || [];
      const initialAccItems = woAccessories.map((acc, idx) => {
        const accKey = `acc-${idx}-${acc.name}`;
        const existing = existingAccRecon[accKey] || {};
        return {
          key: accKey,
          name: acc.name || '',
          unit: acc.unit || 'pcs',
          po_number: acc.po_number || '',
          ordered_qty: acc.quantity || 0,
          received_qty: acc.received_qty || 0,
          extra_qty: acc.extra_qty || 0,
          used_qty: existing.used_qty ?? '',
          remarks: existing.remarks || ''
        };
      });
      setAccessoriesItems(initialAccItems);

      // If existing reconciliation, load it
      if (response.data.existing_reconciliation) {
        setNotes(response.data.existing_reconciliation.notes || "");
        setShippedDate(response.data.existing_reconciliation.shipped_date || new Date().toISOString().split('T')[0]);
        
        // Load shipped quantities from existing reconciliation
        const existingItems = response.data.existing_reconciliation.items || {};
        const updatedItems = initialItems.map(item => {
          const sizeKey = `${item.size}-${item.quality}-${item.gsm}`;
          const existingItem = existingItems[sizeKey];
          return {
            ...item,
            shipped_qty: existingItem?.shipped_qty || item.shipped_qty,
            remarks: existingItem?.remarks || ""
          };
        });
        setItems(updatedItems);
      }
      
      setLoading(false);
    } catch (err) {
      console.error("Failed to fetch reconciliation data:", err);
      setError(err.response?.data?.detail || "Failed to load reconciliation data");
      setLoading(false);
    }
  };

  const handleShippedQtyChange = (index, value) => {
    const newItems = [...items];
    newItems[index].shipped_qty = parseInt(value) || 0;
    setItems(newItems);
  };

  const handleRemarksChange = (index, value) => {
    const newItems = [...items];
    newItems[index].remarks = value;
    setItems(newItems);
  };

  const handleGradingChange = (index, field, value) => {
    const newItems = [...gradingItems];
    newItems[index][field] = value === '' ? '' : parseInt(value) || 0;
    setGradingItems(newItems);
  };

  const handleAccChange = (index, field, value) => {
    const newItems = [...accessoriesItems];
    if (field === 'used_qty') {
      newItems[index][field] = value === '' ? '' : parseInt(value) || 0;
    } else {
      newItems[index][field] = value;
    }
    setAccessoriesItems(newItems);
  };

  const calculateTotals = () => {
    const totalOrdered = items.reduce((sum, item) => sum + item.ordered_qty, 0);
    const totalWeaved = items.reduce((sum, item) => sum + item.weaved_qty, 0);
    const totalDyed = items.reduce((sum, item) => sum + item.dyed_qty, 0);
    const totalStitched = items.reduce((sum, item) => sum + item.stitched_qty, 0);
    const totalShipped = items.reduce((sum, item) => sum + item.shipped_qty, 0);
    const totalVariance = totalOrdered - totalShipped;
    return { totalOrdered, totalWeaved, totalDyed, totalStitched, totalShipped, totalVariance };
  };

  const calculateGradingTotals = () => {
    const totalStitched = gradingItems.reduce((sum, item) => sum + (item.stitched_qty || 0), 0);
    const totalFresh = gradingItems.reduce((sum, item) => sum + (parseInt(item.fresh_pcs) || 0), 0);
    const totalBGrade = gradingItems.reduce((sum, item) => sum + (parseInt(item.b_grade) || 0), 0);
    const totalStain = gradingItems.reduce((sum, item) => sum + (parseInt(item.stain) || 0), 0);
    const totalS2 = gradingItems.reduce((sum, item) => sum + (parseInt(item.s2) || 0), 0);
    const totalGraded = totalFresh + totalBGrade + totalStain + totalS2;
    return { totalStitched, totalFresh, totalBGrade, totalStain, totalS2, totalGraded };
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSuccess("");
    
    try {
      const token = localStorage.getItem('token');
      const { totalOrdered, totalShipped } = calculateTotals();
      
      // Build grading data
      const gradingData = {};
      gradingItems.forEach(item => {
        const sizeKey = `${item.size}-${item.quality}-${item.gsm}`;
        gradingData[sizeKey] = {
          fresh_pcs: parseInt(item.fresh_pcs) || 0,
          b_grade: parseInt(item.b_grade) || 0,
          stain: parseInt(item.stain) || 0,
          s2: parseInt(item.s2) || 0
        };
      });
      
      // Build accessories reconciliation data
      const accReconData = {};
      accessoriesItems.forEach(item => {
        accReconData[item.key] = {
          name: item.name,
          unit: item.unit,
          po_number: item.po_number,
          ordered_qty: item.ordered_qty,
          received_qty: item.received_qty,
          used_qty: parseInt(item.used_qty) || 0,
          remarks: item.remarks
        };
      });

      const payload = {
        wo_id: woId,
        items: items.map(item => ({
          size: item.size,
          quality: item.quality,
          gsm: item.gsm,
          ordered_qty: item.ordered_qty,
          weaved_qty: item.weaved_qty,
          dyed_qty: item.dyed_qty,
          stitched_qty: item.stitched_qty,
          shipped_qty: item.shipped_qty,
          remarks: item.remarks
        })),
        grading: gradingData,
        accessories_reconciliation: accReconData,
        shipped_date: shippedDate,
        total_ordered: totalOrdered,
        total_shipped: totalShipped,
        notes: notes
      };
      
      await axios.post(`${API}/work-orders/${woId}/reconciliation`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess("Reconciliation saved successfully!");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Failed to save reconciliation:", err);
      setError(err.response?.data?.detail || "Failed to save reconciliation");
    } finally {
      setSaving(false);
    }
  };

  const fetchReport = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/work-orders/${woId}/reconciliation/report`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReport(response.data);
      setShowReport(true);
    } catch (err) {
      console.error("Failed to fetch report:", err);
      setError(err.response?.data?.detail || "Failed to load report. Please save reconciliation first.");
    }
  };

  const { totalOrdered, totalWeaved, totalDyed, totalStitched, totalShipped, totalVariance } = calculateTotals();
  const { totalFresh, totalBGrade, totalStain, totalS2, totalGraded } = calculateGradingTotals();

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center h-full">
        <div className="text-muted-foreground">Loading reconciliation data...</div>
      </div>
    );
  }

  if (!reconciliationData?.is_shipped) {
    return (
      <div className="p-8">
        <Card className="max-w-lg mx-auto">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertTriangle className="w-12 h-12 text-yellow-500 mb-4" />
            <h2 className="text-xl font-bold mb-2">Work Order Not Ready</h2>
            <p className="text-muted-foreground text-center mb-4">
              This work order must be marked as "Ready" or "Shipped" before reconciliation can be done.
            </p>
            <Button onClick={() => navigate(-1)}>Go Back</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-2">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="font-heading font-bold text-2xl text-primary">
            Work Order Reconciliation
          </h1>
          <p className="text-muted-foreground flex items-center gap-2">
            {reconciliationData.wo_number} - {reconciliationData.customer_name}
            {reconciliationData.is_dispatched ? (
              <Badge className="bg-green-600 text-white">Shipped</Badge>
            ) : reconciliationData.is_ready ? (
              <Badge className="bg-amber-500 text-white">Ready</Badge>
            ) : null}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={fetchReport} size="sm" className="text-xs sm:text-sm">
            <FileText className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">View</span> Report
          </Button>
          <Button onClick={handleSave} disabled={saving} size="sm" className="text-xs sm:text-sm">
            <Save className="w-4 h-4 mr-1 sm:mr-2" />
            {saving ? "Saving..." : <><span className="hidden sm:inline">Save</span> Reconciliation</>}
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-destructive/10 border border-destructive/30 text-destructive px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-300 text-green-800 px-4 py-3 rounded-lg flex items-center gap-2">
          <CheckCircle className="w-5 h-5" />
          {success}
        </div>
      )}

      {/* Order Info */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Package className="w-5 h-5" />
            Order Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground">WO Number</Label>
              <p className="font-semibold">{reconciliationData.wo_number}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Customer</Label>
              <p className="font-semibold">{reconciliationData.customer_name}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Customer PO</Label>
              <p className="font-semibold">{reconciliationData.customer_po_number || "N/A"}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Status</Label>
              <Badge variant="default" className="bg-green-600">SHIPPED</Badge>
            </div>
          </div>
          <div className="mt-4">
            <Label className="text-xs text-muted-foreground">Shipped Date</Label>
            <Input 
              type="date" 
              value={shippedDate} 
              onChange={(e) => setShippedDate(e.target.value)}
              className="w-48 mt-1"
            />
          </div>
        </CardContent>
      </Card>

      {/* Tabs for Reconciliation, Grading, and Accessories */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-lg grid-cols-3">
          <TabsTrigger value="reconciliation" className="flex items-center gap-2">
            <ClipboardList className="w-4 h-4" />
            Reconciliation
          </TabsTrigger>
          <TabsTrigger value="grading" className="flex items-center gap-2">
            <Star className="w-4 h-4" />
            Grading
          </TabsTrigger>
          <TabsTrigger value="accessories" className="flex items-center gap-2" data-testid="accessories-recon-tab">
            <Package className="w-4 h-4" />
            Accessories
          </TabsTrigger>
        </TabsList>

        {/* Reconciliation Tab */}
        <TabsContent value="reconciliation" className="space-y-6">
          {/* SKU-wise Reconciliation Table */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Layers className="w-5 h-5" />
                SKU-wise Production & Shipment
              </CardTitle>
              <CardDescription>Each row represents a unique SKU (Size + Design + GSM) as defined in the Work Order</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-bold">Size</TableHead>
                      <TableHead className="font-bold">Design/Quality</TableHead>
                      <TableHead className="font-bold">GSM</TableHead>
                      <TableHead className="text-right font-bold bg-blue-50">Total Order</TableHead>
                      <TableHead className="text-right font-bold bg-amber-50">Weaved Pcs</TableHead>
                      <TableHead className="text-right font-bold bg-purple-50">Dyed Pcs</TableHead>
                      <TableHead className="text-right font-bold bg-green-50">Stitched Pcs</TableHead>
                      <TableHead className="text-right font-bold bg-emerald-100">Shipped Qty</TableHead>
                      <TableHead className="text-right font-bold">Variance</TableHead>
                      <TableHead>Remarks</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item, index) => {
                      const variance = item.ordered_qty - item.shipped_qty;
                      const shortPercent = item.weaved_qty > 0 ? ((item.weaved_qty - item.shipped_qty) / item.weaved_qty) * 100 : 0;
                      const isShortShipment = shortPercent > 3;
                      return (
                        <TableRow key={index} className="hover:bg-slate-50">
                          <TableCell className="font-medium">{item.size}</TableCell>
                          <TableCell className="font-medium">{item.quality}</TableCell>
                          <TableCell>{item.gsm}</TableCell>
                          <TableCell className="text-right font-bold text-blue-700 bg-blue-50/50">
                            {item.ordered_qty.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right font-semibold text-amber-700 bg-amber-50/50">
                            {item.weaved_qty.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right font-semibold text-purple-700 bg-purple-50/50">
                            {item.dyed_qty.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right font-semibold text-green-700 bg-green-50/50">
                            {item.stitched_qty.toLocaleString()}
                          </TableCell>
                          <TableCell className={`text-right ${isShortShipment ? 'bg-red-50' : 'bg-emerald-50/50'}`}>
                            <div className="flex items-center justify-end gap-1">
                              <Input
                                type="number"
                                value={item.shipped_qty}
                                onChange={(e) => handleShippedQtyChange(index, e.target.value)}
                                className={`w-24 text-right font-semibold ${isShortShipment ? 'text-red-700 border-red-300' : ''}`}
                                min="0"
                              />
                              {item.weaved_qty > 0 && (
                                <span className={`text-xs font-bold whitespace-nowrap ${isShortShipment ? 'text-red-600' : 'text-green-600'}`}>
                                  {shortPercent > 0 ? `-${shortPercent.toFixed(1)}%` : '0%'}
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className={`text-right font-bold ${variance > 0 ? 'text-red-600' : variance < 0 ? 'text-green-600' : 'text-slate-500'}`}>
                            {variance > 0 ? `-${variance}` : variance < 0 ? `+${Math.abs(variance)}` : '0'}
                          </TableCell>
                          <TableCell>
                            <Input
                              type="text"
                              value={item.remarks}
                              onChange={(e) => handleRemarksChange(index, e.target.value)}
                              placeholder="Remarks"
                              className="w-28"
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {/* Totals Row */}
                    <TableRow className="bg-slate-100 font-bold border-t-2">
                      <TableCell colSpan={3} className="text-right font-bold">TOTALS:</TableCell>
                      <TableCell className="text-right font-bold text-blue-800">
                        {totalOrdered.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-bold text-amber-800">
                        {totalWeaved.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-bold text-purple-800">
                        {totalDyed.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-bold text-green-800">
                        {totalStitched.toLocaleString()}
                      </TableCell>
                      <TableCell className={`text-right font-bold ${totalWeaved > 0 && ((totalWeaved - totalShipped) / totalWeaved) * 100 > 3 ? 'text-red-700 bg-red-50' : 'text-emerald-800'}`}>
                        {totalShipped.toLocaleString()}
                        {totalWeaved > 0 && (
                          <span className={`text-xs ml-1 ${((totalWeaved - totalShipped) / totalWeaved) * 100 > 3 ? 'text-red-600' : 'text-green-600'}`}>
                            -{(((totalWeaved - totalShipped) / totalWeaved) * 100).toFixed(1)}%
                          </span>
                        )}
                      </TableCell>
                      <TableCell className={`text-right font-bold ${totalVariance > 0 ? 'text-red-700' : totalVariance < 0 ? 'text-green-700' : 'text-slate-600'}`}>
                        {totalVariance > 0 ? `-${totalVariance}` : totalVariance < 0 ? `+${Math.abs(totalVariance)}` : '0'}
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Summary Cards */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Reconciliation Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3 mb-4">
                <div className="bg-blue-100 p-3 sm:p-4 rounded-lg text-center">
                  <div className="text-lg sm:text-2xl font-bold text-blue-700">{totalOrdered.toLocaleString()}</div>
                  <div className="text-xs text-blue-600 font-medium">Total Order</div>
                </div>
                <div className="bg-amber-100 p-3 sm:p-4 rounded-lg text-center">
                  <div className="text-lg sm:text-2xl font-bold text-amber-700">{totalWeaved.toLocaleString()}</div>
                  <div className="text-xs text-amber-600 font-medium">Weaved</div>
                </div>
                <div className="bg-purple-100 p-3 sm:p-4 rounded-lg text-center">
                  <div className="text-lg sm:text-2xl font-bold text-purple-700">{totalDyed.toLocaleString()}</div>
                  <div className="text-xs text-purple-600 font-medium">Dyed</div>
                </div>
                <div className="bg-green-100 p-3 sm:p-4 rounded-lg text-center">
                  <div className="text-lg sm:text-2xl font-bold text-green-700">{totalStitched.toLocaleString()}</div>
                  <div className="text-xs text-green-600 font-medium">Stitched</div>
                </div>
                <div className={`p-3 sm:p-4 rounded-lg text-center ${totalWeaved > 0 && ((totalWeaved - totalShipped) / totalWeaved) * 100 > 3 ? 'bg-red-100' : 'bg-emerald-100'}`}>
                  <div className={`text-lg sm:text-2xl font-bold ${totalWeaved > 0 && ((totalWeaved - totalShipped) / totalWeaved) * 100 > 3 ? 'text-red-700' : 'text-emerald-700'}`}>{totalShipped.toLocaleString()}</div>
                  <div className={`text-xs font-medium ${totalWeaved > 0 && ((totalWeaved - totalShipped) / totalWeaved) * 100 > 3 ? 'text-red-600' : 'text-emerald-600'}`}>
                    Shipped
                    {totalWeaved > 0 && ((totalWeaved - totalShipped) / totalWeaved) * 100 > 3 && (
                      <span className="block text-red-700 font-bold">(-{(((totalWeaved - totalShipped) / totalWeaved) * 100).toFixed(1)}% short)</span>
                    )}
                  </div>
                </div>
                <div className={`p-3 sm:p-4 rounded-lg text-center ${totalVariance > 0 ? 'bg-red-100' : totalVariance < 0 ? 'bg-blue-100' : 'bg-green-100'}`}>
                  <div className={`text-lg sm:text-2xl font-bold ${totalVariance > 0 ? 'text-red-700' : totalVariance < 0 ? 'text-blue-700' : 'text-green-700'}`}>
                    {totalVariance > 0 ? `-${totalVariance}` : totalVariance < 0 ? `+${Math.abs(totalVariance)}` : '0'}
                  </div>
                  <div className="text-xs font-medium text-muted-foreground">Variance</div>
                </div>
              </div>
              
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any notes about this shipment reconciliation..."
                  className="mt-2"
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Grading Tab */}
        <TabsContent value="grading" className="space-y-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Star className="w-5 h-5" />
                SKU-wise Quality Grading
              </CardTitle>
              <CardDescription>Each row represents a unique SKU (Size + Design + GSM) - Enter grading for each SKU</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-bold">Size</TableHead>
                      <TableHead className="font-bold">Design/Quality</TableHead>
                      <TableHead className="font-bold">GSM</TableHead>
                      <TableHead className="text-right font-bold bg-amber-100">Weaved Qty</TableHead>
                      <TableHead className="text-right font-bold bg-cyan-100">Dyed Qty</TableHead>
                      <TableHead className="text-right font-bold bg-slate-100">Stitched Qty</TableHead>
                      <TableHead className="text-right font-bold bg-purple-100">Shipped Qty</TableHead>
                      <TableHead className="text-right font-bold bg-green-100">Fresh Pcs</TableHead>
                      <TableHead className="text-right font-bold bg-yellow-100">B Grade</TableHead>
                      <TableHead className="text-right font-bold bg-orange-100">Stain</TableHead>
                      <TableHead className="text-right font-bold bg-red-100">S2</TableHead>
                      <TableHead className="text-right font-bold bg-blue-100">Balance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {gradingItems.map((item, index) => {
                      // Balance = Dyed - (Shipped + Fresh + B Grade + Stain + S2)
                      const balance = (item.dyed_qty || 0) - ((item.shipped_qty || 0) + (parseInt(item.fresh_pcs) || 0) + (parseInt(item.b_grade) || 0) + (parseInt(item.stain) || 0) + (parseInt(item.s2) || 0));
                      return (
                        <TableRow key={index} className="hover:bg-slate-50">
                          <TableCell className="font-medium">{item.size}</TableCell>
                          <TableCell className="font-medium">{item.quality}</TableCell>
                          <TableCell>{item.gsm}</TableCell>
                          <TableCell className="text-right font-bold text-amber-700 bg-amber-50/50">
                            {item.weaved_qty.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right font-bold text-cyan-700 bg-cyan-50/50">
                            {(item.dyed_qty || 0).toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right font-bold text-slate-700 bg-slate-50/50">
                            {item.stitched_qty.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right font-bold text-purple-700 bg-purple-50/50">
                            {(item.shipped_qty || 0).toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right bg-green-50/50">
                            <Input
                              type="number"
                              value={item.fresh_pcs}
                              onChange={(e) => handleGradingChange(index, 'fresh_pcs', e.target.value)}
                              className="w-20 text-right font-semibold text-green-700"
                              min="0"
                              placeholder=""
                            />
                          </TableCell>
                          <TableCell className="text-right bg-yellow-50/50">
                            <Input
                              type="number"
                              value={item.b_grade}
                              onChange={(e) => handleGradingChange(index, 'b_grade', e.target.value)}
                              className="w-20 text-right font-semibold text-yellow-700"
                              min="0"
                              placeholder=""
                            />
                          </TableCell>
                          <TableCell className="text-right bg-orange-50/50">
                            <Input
                              type="number"
                              value={item.stain}
                              onChange={(e) => handleGradingChange(index, 'stain', e.target.value)}
                              className="w-20 text-right font-semibold text-orange-700"
                              min="0"
                              placeholder=""
                            />
                          </TableCell>
                          <TableCell className="text-right bg-red-50/50">
                            <Input
                              type="number"
                              value={item.s2}
                              onChange={(e) => handleGradingChange(index, 's2', e.target.value)}
                              className="w-20 text-right font-semibold text-red-700"
                              min="0"
                              placeholder=""
                            />
                          </TableCell>
                          <TableCell className={`text-right font-bold bg-blue-50/50 ${balance !== 0 ? 'text-amber-600' : 'text-green-600'}`}>
                            {balance.toLocaleString()}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {/* Totals Row */}
                    <TableRow className="bg-slate-100 font-bold border-t-2">
                      <TableCell colSpan={3} className="text-right font-bold">TOTALS:</TableCell>
                      <TableCell className="text-right font-bold text-amber-800">
                        {gradingItems.reduce((sum, item) => sum + item.weaved_qty, 0).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-bold text-cyan-800">
                        {gradingItems.reduce((sum, item) => sum + (item.dyed_qty || 0), 0).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-bold text-slate-800">
                        {gradingItems.reduce((sum, item) => sum + item.stitched_qty, 0).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-bold text-purple-800">
                        {gradingItems.reduce((sum, item) => sum + (item.shipped_qty || 0), 0).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-bold text-green-800">
                        {totalFresh.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-bold text-yellow-800">
                        {totalBGrade.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-bold text-orange-800">
                        {totalStain.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-bold text-red-800">
                        {totalS2.toLocaleString()}
                      </TableCell>
                      <TableCell className={`text-right font-bold ${(gradingItems.reduce((sum, item) => sum + (item.dyed_qty || 0), 0) - gradingItems.reduce((sum, item) => sum + (item.shipped_qty || 0), 0) - totalFresh - totalBGrade - totalStain - totalS2) !== 0 ? 'text-amber-600' : 'text-green-600'}`}>
                        {(gradingItems.reduce((sum, item) => sum + (item.dyed_qty || 0), 0) - gradingItems.reduce((sum, item) => sum + (item.shipped_qty || 0), 0) - totalFresh - totalBGrade - totalStain - totalS2).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Grading Summary Cards */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Grading Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2 sm:gap-3">
                <div className="bg-amber-100 p-3 sm:p-4 rounded-lg text-center">
                  <div className="text-lg sm:text-2xl font-bold text-amber-700">
                    {gradingItems.reduce((sum, item) => sum + item.weaved_qty, 0).toLocaleString()}
                  </div>
                  <div className="text-xs text-amber-600 font-medium">Total Weaved</div>
                </div>
                <div className="bg-cyan-100 p-3 sm:p-4 rounded-lg text-center">
                  <div className="text-lg sm:text-2xl font-bold text-cyan-700">
                    {gradingItems.reduce((sum, item) => sum + (item.dyed_qty || 0), 0).toLocaleString()}
                  </div>
                  <div className="text-xs text-cyan-600 font-medium">Total Dyed</div>
                </div>
                <div className="bg-slate-100 p-3 sm:p-4 rounded-lg text-center">
                  <div className="text-lg sm:text-2xl font-bold text-slate-700">
                    {gradingItems.reduce((sum, item) => sum + item.stitched_qty, 0).toLocaleString()}
                  </div>
                  <div className="text-xs text-slate-600 font-medium">Total Stitched</div>
                </div>
                <div className="bg-green-100 p-3 sm:p-4 rounded-lg text-center">
                  <div className="text-lg sm:text-2xl font-bold text-green-700">{totalFresh.toLocaleString()}</div>
                  <div className="text-xs text-green-600 font-medium">Fresh Pcs</div>
                  <div className="text-xs text-green-500">
                    ({totalGraded > 0 ? ((totalFresh / totalGraded) * 100).toFixed(1) : 0}%)
                  </div>
                </div>
                <div className="bg-yellow-100 p-3 sm:p-4 rounded-lg text-center">
                  <div className="text-lg sm:text-2xl font-bold text-yellow-700">{totalBGrade.toLocaleString()}</div>
                  <div className="text-xs text-yellow-600 font-medium">B Grade</div>
                  <div className="text-xs text-yellow-500">
                    ({totalGraded > 0 ? ((totalBGrade / totalGraded) * 100).toFixed(1) : 0}%)
                  </div>
                </div>
                <div className="bg-orange-100 p-3 sm:p-4 rounded-lg text-center">
                  <div className="text-lg sm:text-2xl font-bold text-orange-700">{totalStain.toLocaleString()}</div>
                  <div className="text-xs text-orange-600 font-medium">Stain</div>
                  <div className="text-xs text-orange-500">
                    ({totalGraded > 0 ? ((totalStain / totalGraded) * 100).toFixed(1) : 0}%)
                  </div>
                </div>
                <div className="bg-red-100 p-3 sm:p-4 rounded-lg text-center">
                  <div className="text-lg sm:text-2xl font-bold text-red-700">{totalS2.toLocaleString()}</div>
                  <div className="text-xs text-red-600 font-medium">S2</div>
                  <div className="text-xs text-red-500">
                    ({totalGraded > 0 ? ((totalS2 / totalGraded) * 100).toFixed(1) : 0}%)
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Accessories Tab */}
        <TabsContent value="accessories" className="space-y-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Package className="w-5 h-5" />
                Accessories Reconciliation
              </CardTitle>
              <CardDescription>Compare ordered vs received accessories. Enter used/issued quantities.</CardDescription>
            </CardHeader>
            <CardContent>
              {accessoriesItems.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No accessories found for this Work Order
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="font-bold min-w-[140px]">Accessory</TableHead>
                        <TableHead className="font-bold">Unit</TableHead>
                        <TableHead className="font-bold">PO No.</TableHead>
                        <TableHead className="text-right font-bold bg-blue-50">Ordered</TableHead>
                        <TableHead className="text-right font-bold bg-green-50">Received</TableHead>
                        <TableHead className="text-right font-bold">Extra / Short</TableHead>
                        <TableHead className="text-right font-bold bg-amber-50">Used / Issued</TableHead>
                        <TableHead className="text-right font-bold bg-purple-50">Balance in Hand</TableHead>
                        <TableHead className="min-w-[140px]">Remarks</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {accessoriesItems.map((acc, index) => {
                        const diff = acc.received_qty - acc.ordered_qty;
                        const usedQty = parseInt(acc.used_qty) || 0;
                        const balanceInHand = acc.received_qty - usedQty;
                        return (
                          <TableRow key={index} className="hover:bg-slate-50" data-testid={`acc-recon-row-${index}`}>
                            <TableCell className="font-medium">{acc.name}</TableCell>
                            <TableCell className="text-muted-foreground">{acc.unit}</TableCell>
                            <TableCell>
                              {acc.po_number ? (
                                <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-300">{acc.po_number}</Badge>
                              ) : (
                                <span className="text-muted-foreground text-xs">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right font-semibold text-blue-700 bg-blue-50/50">
                              {acc.ordered_qty.toLocaleString()}
                            </TableCell>
                            <TableCell className={`text-right font-semibold bg-green-50/50 ${acc.received_qty > acc.ordered_qty ? 'text-red-600' : 'text-green-700'}`}>
                              {acc.received_qty.toLocaleString()}
                            </TableCell>
                            <TableCell className="text-right">
                              {diff > 0 ? (
                                <span className="text-red-600 font-bold" data-testid={`acc-extra-${index}`}>+{diff.toLocaleString()} extra</span>
                              ) : diff < 0 ? (
                                <span className="text-orange-600 font-bold" data-testid={`acc-short-${index}`}>{diff.toLocaleString()} short</span>
                              ) : (
                                <span className="text-green-600 font-medium">0</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right bg-amber-50/50">
                              <Input
                                type="number"
                                value={acc.used_qty}
                                onChange={(e) => handleAccChange(index, 'used_qty', e.target.value)}
                                className={`w-24 text-right font-semibold ${usedQty > acc.received_qty ? 'text-red-700 border-red-300' : ''}`}
                                min="0"
                                placeholder="0"
                                data-testid={`acc-used-qty-${index}`}
                              />
                            </TableCell>
                            <TableCell className={`text-right font-bold bg-purple-50/50 ${balanceInHand < 0 ? 'text-red-600' : balanceInHand > 0 ? 'text-blue-600' : 'text-green-600'}`}>
                              {usedQty > 0 ? balanceInHand.toLocaleString() : '-'}
                            </TableCell>
                            <TableCell>
                              <Input
                                value={acc.remarks}
                                onChange={(e) => handleAccChange(index, 'remarks', e.target.value)}
                                className="w-32 text-sm"
                                placeholder="Remarks..."
                                data-testid={`acc-remarks-${index}`}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {/* Totals Row */}
                      <TableRow className="bg-slate-100 font-bold border-t-2">
                        <TableCell colSpan={3} className="text-right font-bold">TOTALS:</TableCell>
                        <TableCell className="text-right font-bold text-blue-800">
                          {accessoriesItems.reduce((sum, a) => sum + a.ordered_qty, 0).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-bold text-green-800">
                          {accessoriesItems.reduce((sum, a) => sum + a.received_qty, 0).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-bold">
                          {(() => {
                            const totalDiff = accessoriesItems.reduce((sum, a) => sum + (a.received_qty - a.ordered_qty), 0);
                            return totalDiff > 0 ? (
                              <span className="text-red-600">+{totalDiff.toLocaleString()}</span>
                            ) : totalDiff < 0 ? (
                              <span className="text-orange-600">{totalDiff.toLocaleString()}</span>
                            ) : (
                              <span className="text-green-600">0</span>
                            );
                          })()}
                        </TableCell>
                        <TableCell className="text-right font-bold text-amber-800">
                          {accessoriesItems.reduce((sum, a) => sum + (parseInt(a.used_qty) || 0), 0).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-bold text-purple-800">
                          {(() => {
                            const totalBal = accessoriesItems.reduce((sum, a) => sum + (a.received_qty - (parseInt(a.used_qty) || 0)), 0);
                            return totalBal.toLocaleString();
                          })()}
                        </TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Accessories Summary Cards */}
          {accessoriesItems.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Accessories Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="bg-blue-100 p-3 sm:p-4 rounded-lg text-center">
                    <div className="text-lg sm:text-2xl font-bold text-blue-700">
                      {accessoriesItems.length}
                    </div>
                    <div className="text-xs text-blue-600 font-medium">Total Items</div>
                  </div>
                  <div className="bg-red-100 p-3 sm:p-4 rounded-lg text-center">
                    <div className="text-lg sm:text-2xl font-bold text-red-700">
                      {accessoriesItems.filter(a => a.received_qty > a.ordered_qty).length}
                    </div>
                    <div className="text-xs text-red-600 font-medium">Extra Received</div>
                  </div>
                  <div className="bg-orange-100 p-3 sm:p-4 rounded-lg text-center">
                    <div className="text-lg sm:text-2xl font-bold text-orange-700">
                      {accessoriesItems.filter(a => a.received_qty < a.ordered_qty).length}
                    </div>
                    <div className="text-xs text-orange-600 font-medium">Short Received</div>
                  </div>
                  <div className="bg-green-100 p-3 sm:p-4 rounded-lg text-center">
                    <div className="text-lg sm:text-2xl font-bold text-green-700">
                      {accessoriesItems.filter(a => a.received_qty === a.ordered_qty).length}
                    </div>
                    <div className="text-xs text-green-600 font-medium">Exact Match</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Report Modal */}
      {showReport && report && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-card border border-border rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-heading font-bold text-xl">Reconciliation Report</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowReport(false)}>✕</Button>
            </div>
            
            <div className="space-y-4">
              {/* Report Header */}
              <div className="bg-slate-50 p-4 rounded-lg">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">WO Number:</span>
                    <p className="font-semibold">{report.wo_number}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Customer:</span>
                    <p className="font-semibold">{report.customer_name}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Customer PO:</span>
                    <p className="font-semibold">{report.customer_po_number || "N/A"}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Shipped Date:</span>
                    <p className="font-semibold">{report.shipped_date}</p>
                  </div>
                </div>
              </div>

              {/* Items Table */}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Size</TableHead>
                    <TableHead>Quality</TableHead>
                    <TableHead>GSM</TableHead>
                    <TableHead className="text-right">Ordered</TableHead>
                    <TableHead className="text-right">Shipped</TableHead>
                    <TableHead className="text-right">Variance</TableHead>
                    <TableHead>Remarks</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.items.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">{item.size}</TableCell>
                      <TableCell>{item.quality}</TableCell>
                      <TableCell>{item.gsm}</TableCell>
                      <TableCell className="text-right">{item.ordered_qty}</TableCell>
                      <TableCell className="text-right">{item.shipped_qty}</TableCell>
                      <TableCell className={`text-right font-semibold ${item.variance > 0 ? 'text-red-600' : item.variance < 0 ? 'text-green-600' : ''}`}>
                        {item.variance > 0 ? `-${item.variance}` : item.variance < 0 ? `+${Math.abs(item.variance)}` : '0'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{item.remarks}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Summary */}
              <div className="bg-slate-100 p-4 rounded-lg">
                <h3 className="font-semibold mb-3">Summary</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <span className="text-muted-foreground text-sm">Total Ordered:</span>
                    <p className="font-bold text-lg">{report.summary.total_ordered}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground text-sm">Total Shipped:</span>
                    <p className="font-bold text-lg text-green-600">{report.summary.total_shipped}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground text-sm">Variance:</span>
                    <p className={`font-bold text-lg ${report.summary.total_variance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {report.summary.total_variance > 0 ? `-${report.summary.total_variance}` : report.summary.total_variance}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground text-sm">Variance %:</span>
                    <p className={`font-bold text-lg ${report.summary.variance_percentage > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {report.summary.variance_percentage}%
                    </p>
                  </div>
                </div>
              </div>

              {report.notes && (
                <div>
                  <Label>Notes:</Label>
                  <p className="text-sm mt-1">{report.notes}</p>
                </div>
              )}

              <div className="text-xs text-muted-foreground">
                Reconciled by: {report.reconciled_by} on {new Date(report.reconciled_at).toLocaleString()}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
